import React from "react";
import {
  Alert,
  Badge,
  Button,
  Container,
  Header,
  KeyValuePairs,
  SpaceBetween,
  Spinner,
  TextContent,
} from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { useDlqItemLookup, useResolveRow } from "../../hooks/useInvestigations";
import { InvestigationStatusChip } from "./InvestigationStatusChip";
import { OpenInvestigationButton } from "./OpenInvestigationButton";
import {
  RowSeed,
  autoPreviewEnabled,
  formatTimestamp,
  ownerLabel,
  sampleToSeed,
} from "./investigationUtils";
import { TransactionLink } from "./TransactionLink";
import { DlqPkCell } from "./DlqEventModal";
import { CreatedSummary, QuickCreate } from "./QuickCreate";
import { LoadingLine } from "./LoadingLine";

interface RowActionResolverProps {
  stage: Stage;
  seed: RowSeed;
  onStart: (seed: RowSeed, recurrenceOf?: string) => void;
  onBack: () => void;
  /** Label of the back link; names where it leads (work queue, item lookup, lookup results). */
  backLabel?: string;
  /** Receives the investigation created by the one-click path (auto-preview stages only). */
  onCreated?: (investigationId: string, summary: CreatedSummary) => void;
}

/**
 * Always queries the live registry for a dashboard row before offering to create anything –
 * QuickSight owner/status fields are a delayed projection and may be stale. Existing
 * investigations open in a new tab so the row being triaged is never lost.
 */
export const RowActionResolver: React.FC<RowActionResolverProps> = ({
  stage,
  seed: inputSeed,
  onStart,
  onBack,
  backLabel = "Back to work queue",
  onCreated,
}) => {
  // A manually pasted PK carries no snapshot fields; look the item up so the display and the
  // suggested rule are seeded from the real row.
  const needsLookup =
    !inputSeed.eventsourcekey &&
    !inputSeed.error_type &&
    !inputSeed.error_message;
  const lookup = useDlqItemLookup(
    stage,
    needsLookup ? { dlqPk: inputSeed.dlqPk } : null
  );
  const seed: RowSeed = React.useMemo(() => {
    if (!lookup.sample) return inputSeed;
    // The looked-up row carries every rule field; keep whatever the caller already knew.
    const fromSample = sampleToSeed(lookup.sample);
    return {
      ...inputSeed,
      ...Object.fromEntries(
        Object.entries(fromSample).filter(([, v]) => v !== undefined)
      ),
      dlqPk: inputSeed.dlqPk,
    };
  }, [inputSeed, lookup.sample]);
  // transaction_id / order_id are display-only and never part of a rule, so keep them out of the
  // seed sent to the registry.
  const {
    dlqPk,
    transaction_id: transactionId,
    order_id: orderId,
    ...seedFields
  } = seed;
  const resolution = useResolveRow(stage, {
    dlqPk,
    seed: Object.fromEntries(
      Object.entries(seedFields).filter(([, v]) => v !== undefined)
    ) as Record<string, string>,
  });

  return (
    <SpaceBetween size="m">
      <Container
        header={
          <Header
            variant="h3"
            description={`Live registry lookup for ${stage}`}
          >
            DLQ item
          </Header>
        }
      >
        <KeyValuePairs
          columns={2}
          items={[
            {
              label: "DLQ PK",
              value: (
                <DlqPkCell
                  stage={stage}
                  dlqPk={dlqPk}
                  transactionId={transactionId}
                  orderId={orderId}
                />
              ),
            },
            {
              label: "Transaction",
              value: lookup.pending ? (
                "…"
              ) : (
                <TransactionLink
                  stage={stage}
                  transactionId={transactionId}
                  orderId={orderId}
                />
              ),
            },
            { label: "Queue", value: seed.eventsourcekey || "—" },
            { label: "Error type", value: seed.error_type || "—" },
            { label: "Category", value: seed.error_category || "—" },
          ]}
        />
        {seed.error_message && (
          <TextContent>
            <small style={{ wordBreak: "break-word" }}>
              {seed.error_message}
            </small>
          </TextContent>
        )}
        {lookup.pending && (
          <TextContent>
            <small>
              <Spinner size="normal" /> Fetching the item&apos;s snapshot
              fields…
            </small>
          </TextContent>
        )}
        {lookup.notFound && (
          <Alert type="warning">
            This DLQ PK is not in the latest hourly snapshot; it may be newer
            than the snapshot or already drained. The registry lookup below is
            still authoritative.
          </Alert>
        )}
        {lookup.error && (
          <Alert type="error">Item lookup failed: {lookup.error.message}</Alert>
        )}
      </Container>

      {resolution.isLoading && (
        <LoadingLine>Checking the live investigation registry…</LoadingLine>
      )}
      {resolution.error && (
        <Alert type="error" header="Registry unavailable">
          {resolution.error.message}. You can still use the dashboard; try again
          in a moment.
        </Alert>
      )}
      {resolution.data && resolution.data.state === "uninvestigated" && (
        <Container>
          <SpaceBetween size="s">
            <Alert type="info">
              No investigation currently covers this item.
            </Alert>
            {autoPreviewEnabled(stage) && onCreated && !lookup.pending ? (
              <QuickCreate
                stage={stage}
                seed={seed}
                onCustomise={() => onStart(seed)}
                onCreated={onCreated}
              />
            ) : (
              <Button variant="primary" onClick={() => onStart(seed)}>
                Start investigation
              </Button>
            )}
          </SpaceBetween>
        </Container>
      )}
      {resolution.data &&
        resolution.data.state === "active" &&
        resolution.data.primary && (
          <Container
            header={<Header variant="h3">Already being investigated</Header>}
          >
            <SpaceBetween size="s">
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <strong>{resolution.data.primary.title}</strong>
                <InvestigationStatusChip
                  status={resolution.data.primary.status}
                />
                <Badge>{ownerLabel(resolution.data.primary.owner)}</Badge>
                {resolution.data.assignmentPending && (
                  <Badge color="grey">
                    assignment pending next reconciliation
                  </Badge>
                )}
              </SpaceBetween>
              {resolution.data.primary.blocker?.reason && (
                <Alert type="error">
                  Blocked: {resolution.data.primary.blocker.reason}
                </Alert>
              )}
              <OpenInvestigationButton
                stage={stage}
                investigationId={resolution.data.primary.id}
                title={resolution.data.primary.title}
                variant="primary"
                ariaLabel={`Open investigation ${resolution.data.primary.title} in a new tab`}
              >
                Open investigation
              </OpenInvestigationButton>
            </SpaceBetween>
          </Container>
        )}
      {resolution.data && resolution.data.state === "resolved" && (
        <Container header={<Header variant="h3">Previously resolved</Header>}>
          <SpaceBetween size="s">
            {(resolution.data.history || [])
              .filter((h) => h.status !== "WITHDRAWN")
              .map((h) => (
                <Container key={h.investigationId}>
                  <SpaceBetween size="xs">
                    <SpaceBetween
                      direction="horizontal"
                      size="xs"
                      alignItems="center"
                    >
                      <strong>{h.title}</strong>
                      <InvestigationStatusChip status={h.status} />
                      <Badge>{ownerLabel(h.owner)}</Badge>
                      {h.resolutionReason && (
                        <Badge color="green">
                          {h.resolutionReason.replace(/_/g, " ").toLowerCase()}
                        </Badge>
                      )}
                    </SpaceBetween>
                    <TextContent>
                      <p>
                        <strong>Conclusion:</strong>{" "}
                        {h.conclusion || "No conclusion recorded"}
                        <br />
                        <small>Resolved {formatTimestamp(h.resolvedAt)}</small>
                      </p>
                    </TextContent>
                    <SpaceBetween direction="horizontal" size="xs">
                      <OpenInvestigationButton
                        stage={stage}
                        investigationId={h.investigationId}
                        title={h.title}
                        variant="normal"
                        ariaLabel={`Open ${
                          h.title ?? h.investigationId ?? "investigation"
                        } in a new tab to reopen it`}
                      >
                        Reopen investigation
                      </OpenInvestigationButton>
                      <Button
                        variant="primary"
                        onClick={() => onStart(seed, h.investigationId)}
                      >
                        Create linked recurrence
                      </Button>
                    </SpaceBetween>
                  </SpaceBetween>
                </Container>
              ))}
          </SpaceBetween>
        </Container>
      )}
      <Button variant="link" iconName="angle-left" onClick={onBack}>
        {backLabel}
      </Button>
    </SpaceBetween>
  );
};

export default RowActionResolver;
